import {
  CalendarDay,
  EntryTone,
  KindVisual,
} from '@backrs_ui/common-ui/src/molecules/MonthCalendar/monthCalendar.types';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  PlanDay,
  PlanDayKind,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

const KINDS = appStrings.modules.satActBooster.calendar.kinds;

/**
 * Section → calendar tone. English and Reading & Writing share the verbal (blue) family; Math is
 * one tone across both tests. Unmapped sections fall back to the brand tone.
 */
const SECTION_TONES: Record<string, EntryTone> = {
  English: 'blue',
  'Reading and Writing': 'blue',
  Math: 'yellow',
  Reading: 'brand',
  Science: 'green',
};

export const toneForSection = (section: string): EntryTone => SECTION_TONES[section] ?? 'brand';

/**
 * Visuals for the non-study day kinds; study days render their topic entries instead of a pill.
 * off = blackout · timed = every day of each 3rd week (rotating full-practice sections) · test_day = the exam date.
 */
const KIND_VISUALS: Partial<Record<PlanDayKind, KindVisual>> = {
  rest: {
    label: KINDS.rest,
    bg: 'Gray/50',
    fg: 'Colors/Text/text-secondary',
    border: 'Gray/200',
    dot: 'Colors/Foreground/fg-secondary',
  },
  off: {
    label: KINDS.off,
    bg: 'Colors/Background/bg-disabled-subtle',
    fg: 'Colors/Text/text-disabled',
    dot: 'Colors/Text/text-disabled',
  },
  timed: {
    label: KINDS.timed,
    bg: 'Orange/50',
    fg: 'Orange/700',
    border: 'Orange/200',
    dot: 'Orange/500',
    icon: 'Clock',
  },
  test_day: {
    label: KINDS.test_day,
    bg: 'Colors/Background/bg-brand-solid',
    fg: 'Colors/Text/text-white',
    dot: 'Colors/Background/bg-brand-solid',
    icon: 'Trophy01',
  },
};

/** Kinds that carry a task and can fall due/overdue; rest/off days never do. */
const ACTIONABLE_KINDS: ReadonlySet<PlanDayKind> = new Set<PlanDayKind>([
  'study',
  'timed',
  'test_day',
]);

/**
 * Shortens the official score-category names to fit the pill's narrow column; every other surface
 * shows the stored name. Never apply it to `entry.section`, which SECTION_TONES matches exactly.
 */
const shortenEntryLabel = (label: string): string => label.replace(/ and /g, ' & ');

/** Adapt an engine plan day to the calendar's day model, coloring each entry by its section. */
export const planDayToCalendarDay = (day: PlanDay): CalendarDay => ({
  date: day.date,
  kindVisual: KIND_VISUALS[day.kind],
  actionable: ACTIONABLE_KINDS.has(day.kind),
  completed: day.completed,
  note: day.note,
  entries: day.entries.map((entry) => ({
    label: shortenEntryLabel(entry.label),
    tone: toneForSection(entry.section),
  })),
});
